import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useAuth } from '@/contexts/AuthContext'
import {
  clearStoredBranchId,
  resolveInitialBranchId,
  storeBranchId,
} from '@/lib/branch'
import { setApiBranchId } from '@/lib/api'

const BranchContext = createContext(null)

export function BranchProvider({ children }) {
  const { user } = useAuth()
  const [activeBranchId, setActiveBranchId] = useState(null)

  useEffect(() => {
    if (!user) {
      setActiveBranchId(null)
      setApiBranchId(null)
      clearStoredBranchId()
      return
    }

    const nextId = resolveInitialBranchId(user)
    setActiveBranchId(nextId)
    setApiBranchId(nextId)
    if (nextId) {
      storeBranchId(nextId)
    }
  }, [user])

  const branches = user?.branches ?? []

  const activeBranch = useMemo(() => {
    if (!activeBranchId) {
      return user?.default_branch ?? null
    }

    return (
      branches.find((branch) => branch.id === activeBranchId) ??
      user?.default_branch ??
      null
    )
  }, [activeBranchId, branches, user?.default_branch])

  const setActiveBranch = useCallback(
    (branchId) => {
      if (!branches.some((branch) => branch.id === branchId)) {
        return
      }

      setActiveBranchId(branchId)
      setApiBranchId(branchId)
      storeBranchId(branchId)
    },
    [branches],
  )

  const value = useMemo(
    () => ({
      branches,
      activeBranchId,
      activeBranch,
      setActiveBranch,
      canSwitchBranch: branches.length > 1,
    }),
    [branches, activeBranchId, activeBranch, setActiveBranch],
  )

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>
}

/** @returns {NonNullable<import('react').ContextType<typeof BranchContext>>} */
export function useBranch() {
  const context = useContext(BranchContext)
  if (!context) {
    throw new Error('useBranch must be used within BranchProvider')
  }
  return context
}
